import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { useFieldDefinitions, type FieldDefinition } from '../contexts/FieldDefinitionsContext';
import {
  applyRegistryTitles,
  buildProPickerFields,
  resolveFieldAliases,
  resolveRegistryTitles,
  type CoveredFields,
  type ProPickerField,
} from '../components/proCardColumns';
import { generateColumnDef } from '../utils/dynamicColumns';
import type { FieldAliasMap } from '../utils/fieldAliases';
import userSettingsService from '../services/userSettings.service';

/**
 * Let a pro/order-based grid offer the whole orders field registry as columns.
 *
 * The dispatch and LC dashboards have had this for a while, because their queries
 * are registry-built end to end. Everything else — Repair Orders, the Templates
 * table, the Dispatch Planner — builds its own SQL, so it gets the columns the
 * other way round: the backend attaches them to rows it has already fetched
 * (`app/utils/entity_enrichment.py`), and this hook is the client half.
 *
 * A host wires three things:
 *
 *     const { columns, availableFields, additionalFields,
 *             onAdditionalFieldsChange, ready } = useAdditionalFields({
 *       settingsKey: 'repair-orders', baseColumns: REPAIR_ORDER_COLUMNS,
 *     });
 *
 * ...spreads the first four onto its TanTable, and holds its list fetch until
 * `ready` (see below). The added fieldnames go to the endpoint as
 * `additional_fields`.
 *
 * The two halves are also exported separately, because the selection is an input
 * to the FETCH while the columns are an input to the GRID — and a component that
 * declares its fetch above its (long) column list can't call one hook that needs
 * both. `useAdditionalFieldSelection` goes up with the fetch,
 * `useAdditionalFieldColumns` goes down with the columns.
 *
 * THREE CONTRACTS IT EXISTS TO BRIDGE
 *
 *  1. PERSISTENCE. TanTable saves column visibility/order/sizing under its own
 *     `settingsKey` but deliberately NOT `additionalFields` — that set belongs to
 *     the host, because the host is what fetches the data. Every surface that has
 *     ever offered dynamic columns has had to remember them itself; this is that
 *     code, written once. Without it an added column silently vanishes on reload.
 *
 *  2. THE NAME MAPPING. A field's registry name and its grid column id are not
 *     always equal — seven names round-trip through FIELD_NAME_MAPPING
 *     (`currentlocationname` -> `currentlocation`, ...). The backend already
 *     remaps the value it attaches, so the column generated here has to key off
 *     the SAME mapped name or it reads a key that is never populated and renders
 *     permanently blank. `generateColumnDef` applies that mapping, which is why
 *     columns are built through it rather than by hand.
 *
 *  3. THE NAMES. A grid that already has a column for a registry field doesn't get
 *     offered that field again — it keeps its own column, so any saved layout or
 *     rule built on that column id survives. What it does NOT keep is its
 *     hand-written label: the column takes the registry's short name as its header
 *     and its long name as the picker label, which is the whole reason one field
 *     stopped reading as "Return To" here and "Terminate" there. Titling and
 *     de-duplication are decided by the same function (`resolveRegistryTitles`) so
 *     they cannot disagree about which column covers which field. A column whose id
 *     collides with a field it does NOT actually show opts out with
 *     `meta: { registryTitle: false }`.
 *
 * IF YOUR HOST TRANSFORMS ITS ROWS before handing them to TanTable — grouping,
 * pivoting, or rebuilding them from an explicit field list — it has to carry the
 * attached keys across itself. The backend attaches them to the API rows; a
 * transform that names its output fields one by one silently drops them, and the
 * column renders blank with the data sitting right there in the network tab.
 * `additionalFields.map(getApiFieldName)` is the set to copy. Check BOTH ends: Street
 * Turn drops them when it groups rows for the grid, and Smart Dispatch dropped them
 * in its FETCH, where the response is remapped onto an Order interface field by field
 * long before any grid code runs.
 *
 * ROW EXPANSION IS OPT-IN, and matches the backend default: a field whose join is
 * one-to-many with the order (invoice line items) turns one order into N grid
 * rows. Hosts whose row identity can't tolerate duplicates — which is most of
 * them, and every virtualized grid keyed by pronumber — leave `allowExpanding`
 * off and never see those fields offered.
 */

/**
 * Synthetic, non-registry columns a host also offers through the picker — the
 * dashboards' Service Failures (`sf`) / Trouble Tickets (`tt`). They ride in
 * `additionalFields` like any other pick and go to the backend the same way (which
 * strips the sentinels and attaches the badge data), but they have no field
 * definition: the hook keeps them through its "known field" filter, lists `picker`
 * ahead of the registry fields, and uses the host's own column for each instead of
 * generating one. Pass a stable reference (module constant or memo).
 */
export interface ComputedFieldSet<T> {
  /** Picker rows, in the `{fieldname, displayname, abbreviation, type}` shape of
   *  COMPUTED_FIELD_OPTIONS. */
  picker: ReadonlyArray<Omit<ProPickerField, 'expands'> & { expands?: boolean }>;
  /** The host's column def per computed field id. */
  columns: Readonly<Record<string, ColumnDef<T>>>;
}

export interface UseAdditionalFieldsOptions<T> {
  /** Namespaces the saved selection. Use the host's TanTable settingsKey. `null`
   *  means there is currently no grid to remember for (the LC card view keys by
   *  the OPEN bucket): nothing is loaded or saved and the selection reads empty. */
  settingsKey: string | null;
  /** Synthetic columns offered alongside the registry fields — see ComputedFieldSet. */
  computedFields?: ComputedFieldSet<T>;
  /** The grid's own columns. Returned back as `columns` with the extras appended. */
  baseColumns: ColumnDef<T>[];
  /** Registry fields one of `baseColumns` already renders under a DIFFERENT id,
   *  which the picker's id/mapped-name rules structurally cannot see. Without this
   *  such a field is offered a second time, as a duplicate column. Omit it and
   *  nothing is hidden — a host must opt into hiding a field, never inherit it.
   *
   *  Write it as a record (`{ pro_bol: ['bol'] }`): naming the column that covers
   *  each field is what lets that column take the registry's title. A bare Set still
   *  de-duplicates, it just leaves those columns under their own headers. */
  coveredFields?: CoveredFields;
  /** Column ids the grid always shows, so the picker shouldn't list them. */
  alwaysShownColumnIds?: ReadonlyArray<string>;
  /** Allow fields that fan one order into several rows. Off by default. */
  allowExpanding?: boolean;
  /** Which table binding this grid reads — 'templates' for the Templates grids,
   *  omitted for the order-backed ones. Fields the backend marks unavailable under
   *  it are not offered: a template has no trouble tickets, and the `template` table
   *  lacks a few of orderscurrent's columns, so those fields have no answer there. */
  binding?: string;
  /** Identifies a row that is a CHILD of another row — a leg beneath its order on
   *  the Dispatch Planner. Every attached column renders blank on those rows.
   *
   *  Not a style choice: an attached value is resolved per-ORDER, and that is true
   *  even of the fields that come off a leg. `d_destapptdate` resolves the DELIVERY
   *  leg specifically, not "whichever leg this sub-row happens to be" — so printing
   *  it on a pickup-leg sub-row would show the wrong leg's appointment as if it were
   *  that leg's. Blank is both the correct answer and the precedent the planner's
   *  hand-written order-sourced columns already set (`isSubRow ? null : value`).
   *
   *  Blanks the RENDER only, matching those columns: the row still carries the
   *  parent's value for sorting and filtering. */
  isSubRow?: (row: T) => boolean;
}

export interface AdditionalFieldSelection {
  /** The user's added registry fieldnames — send as `additional_fields`. */
  additionalFields: string[];
  onAdditionalFieldsChange: (fields: string[]) => void;
  /** False until the saved selection is known. Hold the first list fetch on this,
   *  or it runs twice — once without the extra columns, once with. */
  ready: boolean;
}

export interface AdditionalFieldColumns<T> {
  /** `baseColumns` plus a generated column per added field. Feed to TanTable. */
  columns: ColumnDef<T>[];
  /** TanTable's picker universe: the grid's own columns plus every addable field. */
  availableFields: ProPickerField[];
  /** What this grid's column ids mean in registry terms. Pass to TanTable as
   *  `fieldAliases` so a View saved on a grid that spells a field differently
   *  still resolves onto these columns — see FieldAliasMap. */
  fieldAliases: FieldAliasMap;
  /** The user's added registry fieldnames — send as `additional_fields`. */
  additionalFields: string[];
  onAdditionalFieldsChange: (fields: string[]) => void;
  /** False until the saved selection is known. Hold the first list fetch on this,
   *  or it runs twice — once without the extra columns, once with. */
  ready: boolean;
}

/** A host hides nothing from the picker unless it says so. buildProPickerFields
 *  defaults to the PRO CARD's covered set — right for SearchResults, which is what
 *  that module is — but inheriting it here quietly withheld 22 registry fields from
 *  every host that passed no set of its own, including Last Free Day, Cutoff, Doc
 *  Cutoff and Per Diem LFD. Those are covered on the PRO card by a static column of
 *  its own; on a CST or Unbilled Charges grid there is no such column, so there was
 *  nothing to be covered BY and the fields were simply missing. */
const NOTHING_COVERED: ReadonlySet<string> = new Set();

const settingKeyFor = (key: string) => `additional_fields_${key}`;

/** Last known selection, mirrored locally so a repeat visit doesn't hold the grid
 *  behind a settings round-trip. The server row stays authoritative. */
const readCached = (key: string): string[] | null => {
  try {
    const raw = localStorage.getItem(settingKeyFor(key));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCached = (key: string, fields: string[]): void => {
  try {
    localStorage.setItem(settingKeyFor(key), JSON.stringify(fields));
  } catch {
    /* private mode / quota — the server row still has it */
  }
};

/** Which registry fields this grid may use at all: displayable, and — unless the
 *  host opted in — not one whose join fans the row. Shared by both halves so a
 *  stale saved selection can't resurrect an expanding column after a host turns
 *  the option back off. */
function useOfferableFields(allowExpanding: boolean, binding?: string) {
  const { fieldDefinitions } = useFieldDefinitions();
  return useMemo(
    () => fieldDefinitions.filter((d) =>
      d.is_displayable
      && (allowExpanding || !d.expands)
      && !(binding && d.unavailable_in?.includes(binding))),
    [fieldDefinitions, allowExpanding, binding],
  );
}

/** The persisted half: what the user added, and whether that is known yet. Pair
 *  with `useAdditionalFieldColumns`, or use `useAdditionalFields` for both. */
/** Wrap a generated column so it renders nothing on sub-rows, deferring to the
 *  column's own cell renderer everywhere else. */
function blankOnSubRows<T>(col: ColumnDef<T>, isSubRow: (row: T) => boolean): ColumnDef<T> {
  const inner = (col as { cell?: (info: any) => unknown }).cell;
  return {
    ...col,
    cell: (info: any) =>
      isSubRow(info.row.original as T)
        ? null
        : (inner ? inner(info) : (info.getValue() as unknown)),
  } as ColumnDef<T>;
}

/** A stable "nothing extra" so hosts that declare no computed fields don't churn
 *  the memo below on every render. */
const NO_COMPUTED: ReadonlyArray<string> = [];

export function useAdditionalFieldSelection(
  settingsKey: string | null,
  {
    allowExpanding = false,
    binding,
    computedFieldnames = NO_COMPUTED,
  }: { allowExpanding?: boolean; binding?: string; computedFieldnames?: ReadonlyArray<string> } = {},
): AdditionalFieldSelection {
  const offerable = useOfferableFields(allowExpanding, binding);
  // null = the saved selection isn't known yet (no local mirror to start from).
  // A null KEY has no selection to know: it reads as empty and ready.
  const [additional, setAdditional] = useState<string[] | null>(
    () => (settingsKey === null ? [] : readCached(settingsKey)),
  );

  useEffect(() => {
    if (settingsKey === null) {
      setAdditional([]);
      return;
    }
    let cancelled = false;
    const cached = readCached(settingsKey);
    // Reset on a key change so a host fetching on `ready` never fires the new
    // key's request with the previous key's fields.
    setAdditional(cached);
    userSettingsService
      .getUserSetting(settingKeyFor(settingsKey))
      .then((s) => {
        if (cancelled) return;
        const saved = Array.isArray(s?.setting_value) ? (s!.setting_value as string[]) : [];
        writeCached(settingsKey, saved);
        // Only disturb the grid when the server actually disagrees with the mirror
        // (the selection was changed on another device).
        if (!cached || cached.join(',') !== saved.join(',')) setAdditional(saved);
      })
      .catch(() => { if (!cancelled && cached === null) setAdditional([]); });
    return () => { cancelled = true; };
  }, [settingsKey]);

  const known = useMemo(
    () => new Set([...offerable.map((d) => d.fieldname), ...computedFieldnames]),
    [offerable, computedFieldnames],
  );
  const additionalFields = useMemo(
    () => (additional === null ? [] : additional.filter((fn) => known.has(fn))),
    [additional, known],
  );

  const onAdditionalFieldsChange = useCallback((next: string[]) => {
    if (settingsKey === null) return;
    setAdditional(next);
    writeCached(settingsKey, next);
    userSettingsService.saveUserSetting(settingKeyFor(settingsKey), next).catch((err) => {
      console.error('Failed to save the added-column selection:', err);
    });
  }, [settingsKey]);

  return { additionalFields, onAdditionalFieldsChange, ready: additional !== null };
}

/** The grid half: the host's columns with a generated one per added field, and the
 *  picker universe to offer. Takes the selection so the two can be called at
 *  different points in a component. */
export function useAdditionalFieldColumns<T>(
  selection: AdditionalFieldSelection,
  {
    baseColumns,
    coveredFields,
    alwaysShownColumnIds,
    allowExpanding = false,
    binding,
    isSubRow,
    computedFields,
  }: Omit<UseAdditionalFieldsOptions<T>, 'settingsKey'>,
): { columns: ColumnDef<T>[]; availableFields: ProPickerField[]; fieldAliases: FieldAliasMap } {
  const offerable = useOfferableFields(allowExpanding, binding);
  const defByName = useMemo(
    () => new Map(offerable.map((d) => [d.fieldname, d])),
    [offerable],
  );
  const { additionalFields } = selection;
  const additionalKey = additionalFields.join(',');

  // Held in a ref, and deliberately NOT a memo dependency. Callers write this as an
  // inline arrow (`isSubRow: (row) => !!row.isSubRow`), which is a new identity every
  // render; as a dependency it rebuilt the columns array every render, and TanTable
  // rebuilds its column defs from that prop — churning the row virtualizer's
  // measurements into blank rows. Only whether a predicate was GIVEN can change the
  // column shape, so that boolean is the dependency.
  const isSubRowRef = useRef(isSubRow);
  isSubRowRef.current = isSubRow;
  const hasSubRows = !!isSubRow;

  // A base column that already renders a registry field takes that field's NAMES —
  // the short one as its header, the long one as its picker label — so the field
  // reads the same here as on every other grid, and as it would if the user had
  // added it from the picker. Only the label moves: the column keeps its id, so
  // saved layouts and anything built on it are untouched. Resolved off `offerable`,
  // the very list the picker de-duplicates against, so a column is re-titled exactly
  // when it displaces a def. Opt a column out with `meta: { registryTitle: false }`.
  //
  // Both of these are keyed on the CONTENT of `coveredFields` /
  // `alwaysShownColumnIds`, not their identity: a host writing either inline is a new
  // object every render, and since the titles feed `columns` now, that would rebuild
  // the column array every render — the exact churn `isSubRow` is held in a ref to
  // avoid. They are small (tens of entries), so stringifying them each render is
  // cheaper than the rebuild it prevents.
  const coveredKey = useMemo(
    () => JSON.stringify(
      !coveredFields ? null
        : coveredFields instanceof Set ? [...coveredFields].sort()
          : coveredFields,
    ),
    [coveredFields],
  );
  const alwaysShownKey = alwaysShownColumnIds?.join(',') ?? '';
  const registryTitles = useMemo(
    () => resolveRegistryTitles(baseColumns, offerable, {
      coveredFields: coveredFields ?? NOTHING_COVERED,
      ...(alwaysShownColumnIds ? { alwaysShownColumnIds } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseColumns, offerable, coveredKey, alwaysShownKey],
  );
  const titledBaseColumns = useMemo(
    () => applyRegistryTitles(baseColumns, registryTitles),
    [baseColumns, registryTitles],
  );
  // The same resolution, kept as ids rather than names: what each base column IS,
  // for translating a saved layout that arrived from a grid spelling it otherwise.
  // Only the base columns need declaring — a picker-added column is id'd by its own
  // field's API name, which prepareTableTemplate reduces to a match unaided.
  const fieldAliases = useMemo(
    () => resolveFieldAliases(baseColumns, offerable, {
      coveredFields: coveredFields ?? NOTHING_COVERED,
      ...(alwaysShownColumnIds ? { alwaysShownColumnIds } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseColumns, offerable, coveredKey, alwaysShownKey],
  );

  const columns = useMemo(() => {
    const extra: ColumnDef<T>[] = [];
    for (const fn of additionalFields) {
      // A computed field is the host's own column, used as-is (it already knows
      // how to blank itself if it needs to); a registry field gets a generated one.
      const own = computedFields?.columns[fn];
      if (own) { extra.push(own); continue; }
      const d = defByName.get(fn);
      if (!d) continue;
      const col = generateColumnDef(d) as ColumnDef<T>;
      extra.push(hasSubRows ? blankOnSubRows(col, (row: T) => !!isSubRowRef.current?.(row)) : col);
    }
    return extra.length ? [...titledBaseColumns, ...extra] : titledBaseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titledBaseColumns, additionalKey, defByName, hasSubRows, computedFields]);

  const availableFields = useMemo(
    () => [
      // Computed fields first, under their full picker names. Once one is added its
      // column is in `columns`, but buildProPickerFields skips added ids, so it is
      // never listed twice.
      ...(computedFields?.picker ?? []).map((p) => ({ ...p, expands: !!p.expands })),
      ...buildProPickerFields({
        columns: columns as ReadonlyArray<{
          id?: string; accessorKey?: string; header?: unknown; meta?: unknown;
        }>,
        fieldDefinitions: offerable,
        additionalFields,
        coveredFields: coveredFields ?? NOTHING_COVERED,
        ...(alwaysShownColumnIds ? { alwaysShownColumnIds } : {}),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, offerable, additionalKey, coveredKey, alwaysShownKey, computedFields],
  );

  return { columns, availableFields, fieldAliases };
}

/** The computed field ids a host declared — the selection hook keeps these through
 *  its "known field" filter. Memoised on the set's identity, so pass a stable one. */
export function useComputedFieldnames<T>(computedFields?: ComputedFieldSet<T>): ReadonlyArray<string> {
  return useMemo(
    () => (computedFields ? computedFields.picker.map((p) => p.fieldname) : NO_COMPUTED),
    [computedFields],
  );
}

export function useAdditionalFields<T>({
  settingsKey,
  allowExpanding = false,
  binding,
  computedFields,
  ...columnOptions
}: UseAdditionalFieldsOptions<T>): AdditionalFieldColumns<T> {
  const computedFieldnames = useComputedFieldnames(computedFields);
  const selection = useAdditionalFieldSelection(settingsKey, { allowExpanding, binding, computedFieldnames });
  const { columns, availableFields, fieldAliases } = useAdditionalFieldColumns<T>(
    selection, { ...columnOptions, allowExpanding, binding, computedFields },
  );
  return { columns, availableFields, fieldAliases, ...selection };
}
